import { t } from "@lingui/core/macro";
import { CheckCircleIcon } from "@phosphor-icons/react";
import { useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute, Outlet, redirect, stripSearchParams } from "@tanstack/react-router";
import { createServerFn } from "@tanstack/react-start";
import { getCookie, setCookie } from "@tanstack/react-start/server";
import { zodValidator } from "@tanstack/zod-adapter";
import type React from "react";
import { useEffect } from "react";
import { type Layout, usePanelRef } from "react-resizable-panels";
import { useDebounceCallback } from "usehooks-ts";
import z from "zod";
import { LoadingScreen } from "@/components/layout/loading-screen";
import { useCSSVariables } from "@/components/resume/hooks/use-css-variables";
import { useResumeStore } from "@/components/resume/store/resume";
import { ResizableGroup, ResizablePanel, ResizableSeparator } from "@/components/ui/resizable";
import { useIsMobile } from "@/hooks/use-mobile";
import { orpc } from "@/integrations/orpc/client";
import { redirectToPlacements } from "@/utils/source-url";
import { BuilderHeader } from "./-components/header";
import { BuilderSidebarLeft } from "./-sidebar/left";
import { BuilderSidebarRight } from "./-sidebar/right";
import { useBuilderSidebar, useBuilderSidebarStore } from "./-store/sidebar";

const builderSearchSchema = z.object({
	openAts: z.boolean().optional().default(false),
});

export const Route = createFileRoute("/builder/$resumeId")({
	component: RouteComponent,
	validateSearch: zodValidator(builderSearchSchema),
	search: {
		middlewares: [stripSearchParams({ openAts: false })],
	},
	beforeLoad: async ({ context }) => {
		if (!context.session) {
			if (context.flags.ssoOnly) {
				redirectToPlacements();
			}
			throw redirect({ to: "/auth/login", replace: true });
		}
		return { session: context.session };
	},
	loader: async ({ params, context }) => {
		const [layout, resume] = await Promise.all([
			getBuilderLayoutServerFn(),
			context.queryClient.ensureQueryData(orpc.resume.getById.queryOptions({ input: { id: params.resumeId } })),
		]);

		return { layout, name: resume.name };
	},
	head: ({ loaderData }) => ({
		meta: loaderData ? [{ title: `${loaderData.name} - Polymath Resume Builder` }] : undefined,
	}),
});

function RouteComponent() {
	const { layout: initialLayout } = Route.useLoaderData();

	const { resumeId } = Route.useParams();
	const { data: resume } = useSuspenseQuery(orpc.resume.getById.queryOptions({ input: { id: resumeId } }));

	const style = useCSSVariables(resume.data);
	const isReady = useResumeStore((state) => state.isReady);
	const initialize = useResumeStore((state) => state.initialize);

	useEffect(() => {
		initialize(resume);
		return () => initialize(null);
	}, [resume, initialize]);

	if (!isReady) return <LoadingScreen />;

	return <BuilderLayout style={style} initialLayout={initialLayout} />;
}

type BuilderLayoutProps = React.ComponentProps<"div"> & {
	initialLayout: Layout;
};

function BuilderLayout({ initialLayout, ...props }: BuilderLayoutProps) {
	const isMobile = useIsMobile();

	const leftSidebarRef = usePanelRef();
	const rightSidebarRef = usePanelRef();

	const setLeftSidebar = useBuilderSidebarStore((state) => state.setLeftSidebar);
	const setRightSidebar = useBuilderSidebarStore((state) => state.setRightSidebar);

	const { getSidebarMaxSize } = useBuilderSidebar((state) => ({
		getSidebarMaxSize: state.getSidebarMaxSize,
	}));

	const onLayoutChange = useDebounceCallback((layout: Layout) => {
		const left = (layout as Record<string, number>).left;
		const artboard = (layout as Record<string, number>).artboard;
		const right = (layout as Record<string, number>).right;
		setBuilderLayoutServerFn({ data: { left, artboard, right } });
	}, 200);

	useEffect(() => {
		if (!leftSidebarRef || !rightSidebarRef) return;

		setLeftSidebar(leftSidebarRef);
		setRightSidebar(rightSidebarRef);
	}, [leftSidebarRef, rightSidebarRef, setLeftSidebar, setRightSidebar]);

	const leftSidebarSize = isMobile ? "0%" : `${initialLayout.left}%`;
	const rightSidebarSize = isMobile ? "0%" : `${initialLayout.right}%`;
	const artboardSize = isMobile ? "100%" : `${initialLayout.artboard}%`;

	const { data: resume } = useSuspenseQuery(
		orpc.resume.getById.queryOptions({ input: { id: Route.useParams().resumeId } }),
	);
	const isLocked = resume.isLocked;

	return (
		<div className="flex h-svh flex-col" {...props}>
			<BuilderHeader />

			<ResizableGroup orientation="horizontal" className="mt-14 flex-1 overflow-hidden" onLayoutChange={onLayoutChange}>
				<ResizablePanel
					id="left"
					collapsible
					collapsedSize="0%"
					panelRef={leftSidebarRef}
					maxSize={`${getSidebarMaxSize("left")}%`}
					minSize="0%"
					defaultSize={leftSidebarSize}
					className="relative h-[calc(100svh-3.5rem)] overflow-hidden"
				>
					<BuilderSidebarLeft />
					{isLocked && (
						<div className="absolute inset-0 z-[100] flex cursor-not-allowed flex-col items-center justify-center gap-3 bg-white/70 backdrop-blur-sm">
							<div className="flex flex-col items-center gap-2 rounded-2xl border border-slate-200 bg-white px-5 py-3.5 text-center shadow-lg">
								<CheckCircleIcon className="size-6 text-emerald-500" />
								<p className="font-bold text-slate-700 text-xs uppercase tracking-wider">{t`View Only`}</p>
								<p className="max-w-[160px] text-[11px] text-slate-400 leading-snug">
									{t`This resume is locked and cannot be edited.`}
								</p>
							</div>
						</div>
					)}
				</ResizablePanel>
				<ResizableSeparator withHandle className="z-50 border-s" />
				<ResizablePanel id="artboard" defaultSize={artboardSize} className="relative h-[calc(100svh-3.5rem)]">
					<Outlet />
				</ResizablePanel>
				<ResizableSeparator withHandle className="z-50 border-e" />
				<ResizablePanel
					id="right"
					collapsible
					collapsedSize="0%"
					panelRef={rightSidebarRef}
					maxSize={`${getSidebarMaxSize("right")}%`}
					minSize="0%"
					defaultSize={rightSidebarSize}
					className="h-[calc(100svh-3.5rem)] overflow-hidden"
				>
					<BuilderSidebarRight />
				</ResizablePanel>
			</ResizableGroup>
		</div>
	);
}

const defaultLayout = { left: 20, artboard: 60, right: 20 };
const BUILDER_LAYOUT_COOKIE_NAME = "builder_layout_v4";

const layoutSchema = z.object({ left: z.number(), artboard: z.number(), right: z.number() }).catch(defaultLayout);

const setBuilderLayoutServerFn = createServerFn({ method: "POST" })
	.inputValidator(layoutSchema)
	.handler(async ({ data }) => {
		setCookie(BUILDER_LAYOUT_COOKIE_NAME, JSON.stringify(data));
	});

const getBuilderLayoutServerFn = createServerFn({ method: "GET" }).handler(async () => {
	try {
		const layout = getCookie(BUILDER_LAYOUT_COOKIE_NAME);
		if (!layout) return defaultLayout;
		return layoutSchema.parse(JSON.parse(layout));
	} catch {
		return defaultLayout;
	}
});
